import '@fortawesome/fontawesome-free/css/all.min.css';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useBlocker, useParams } from '@tanstack/react-router';

// material ui
import { Box, Card, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import { usePostExtractConfigurationCheckMutation } from '@/api/energy-analysis/systemQoSApiSlice';
import {
  useGetEnergyGroupQuery,
  usePutEnergyGroupEnergyTreeNodesMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';
import { useEnergyTreeNode } from '@/features/energyxplorer/hooks/useEnergyTreeNode';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import { useDecoratedUnits } from '../../hooks/useDecoratedUnits';
import { useTreeDraft } from '../../hooks/useTreeDraft';
import AddNodeDialog from './AddNodeDialog.tsx';
import { getButtonContent, getNodeContent } from './chartRenderes';
import DeleteNodeDialog from './DeleteNodeDialog.tsx';
import EditNodeDialog from './EditNodeDialog.tsx';
import InfoNodeDrawer from './InfoNodeDrawer.tsx';
import MoveNodeDialog from './MoveNodeDialog.tsx';
import OrgChartToolbar from './OrgChartToolbar';
import SaveChangesDialog from './SaveChangesDialog.tsx';
import UnsavedChangesDialog from './UnsavedChangesDialog.tsx';

import * as d3 from 'd3';
import { OrgChart } from 'd3-org-chart';

/**
 * OrgChartv2 - Interactive organizational chart component using D3 org chart library
 *
 * Features:
 * - Hierarchical tree visualization with expand/collapse functionality
 * - Manual expansion state preservation across re-renders
 * - Search and type filtering with visual highlighting
 * - Zoom and pan controls with state preservation
 * - CRUD operations (Add, Edit, Move, Delete nodes)
 * - Real-time validation and draft management
 * - Theme support (dark/light mode)
 *
 * @param {Object} props - Component props
 * @param {Array} props.data - Tree data structure for the chart
 * @param {Object} props.units - Units configuration for display
 * @param {Function} props.onOpenFilterDialog - Callback to open filter dialog
 * @returns {JSX.Element} The organizational chart component
 */
const OrgChartComponent = ({ data, units, onOpenFilterDialog }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation('energyxplorer-tree-tab');

  const chartInstanceRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const maxDepthRef = useRef(1);

  const { energyGroupId } = useParams({ strict: false });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );

  const {
    draftTree,
    isDirty,
    discardChanges,
    addNode,
    updateNode,
    moveNode,
    deleteNode,
  } = useTreeDraft(
    data || [],
    { preserveDraftOnServerChange: false },
    selectedEnergyGroup?.energyGroupId ?? null,
  );

  // Decorate units with draftTree to reflect real-time changes
  const decoratedUnits = useDecoratedUnits(units, draftTree);

  const [
    postExtractConfigurationCheck,
    { data: validationResult, isLoading: isValidating, reset: resetValidation },
  ] = usePostExtractConfigurationCheckMutation({
    selectedEnergyGroup,
  });

  const [updateEnergyTreeNodes, { isLoading: isSaving }] =
    usePutEnergyGroupEnergyTreeNodesMutation();

  const { timeRange } = useUrlDateRange();

  const { setNodeId, clearNodeId } = useEnergyTreeNode();
  const { unit } = useEnergyGroupUnit();

  // Local state for tracking the actual clicked node from draftTree
  const [clickedNode, setClickedNode] = useState(null);

  const isPortfolio = selectedEnergyGroup?.type.includes('Portfolio');

  // Calculate available types from tree data - derive during render
  const availableTypes = (() => {
    if (!draftTree?.length) return [];

    const types = new Set();
    draftTree.forEach((node) => {
      if (node.type) {
        types.add(node.type);
      }
    });
    return Array.from(types).sort();
  })();

  const [searchValue, setSearchValue] = useState('');
  const [expandLevel, setExpandLevel] = useState(1); // initial: root + depth 1

  // Track manually expanded nodes to preserve them across re-renders
  // This prevents collapsing when nodes are added above depth 2
  // Use ref to avoid cascading renders in effects
  const manuallyExpandedNodesRef = useRef(new Map());

  // Preserve zoom and pan state across updates
  const chartStateRef = useRef(null);

  const [selectedTypes, setSelectedTypes] = useState(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [addToNodeDialogOpen, setAddToNodeDialogOpen] = useState(false);
  const [addToNode, setAddToNode] = useState(null);
  const [editNodeDialogOpen, setEditNodeDialogOpen] = useState(false);
  const [nodeToEdit, setNodeToEdit] = useState(null);
  const [deleteNodeDialogOpen, setDeleteNodeDialogOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [moveNodeDialogOpen, setMoveNodeDialogOpen] = useState(false);
  const [nodeToMove, setNodeToMove] = useState(null);

  // Navigation blocking for unsaved changes
  const blocker = useBlocker({
    shouldBlockFn: () => {
      console.log('shouldBlockFn called, isDirty:', isDirty);
      return true; // Always block when not disabled
    },
    disabled: !isDirty, // Only enable blocking when there are unsaved changes
    withResolver: true,
  });

  console.log('Blocker status:', blocker.status, 'isDirty:', isDirty);
  const isBlocked = blocker.status === 'blocked';

  // Use refs to ensure D3 handlers always access fresh state
  const handlersRef = useRef();
  // eslint-disable-next-line react-hooks/refs
  handlersRef.current = {
    handleNodeClick: (node) => {
      if (!node?.data?.energyTreeNodeId) return;

      // Find the actual node data from draftTree
      const actualNode = draftTree.find(
        (n) => n.energyTreeNodeId === node.data.energyTreeNodeId,
      );

      // Set both the ID (for compatibility) and the actual node data
      setNodeId(node.data.energyTreeNodeId);
      setClickedNode(actualNode || node.data); // Fallback to node.data if not found in draft
      setDialogOpen(true);
    },
    openAdd: (nodeId) => {
      setNodeId(nodeId);
      const node = draftTree.find((n) => n.energyTreeNodeId === nodeId) ?? null;
      setAddToNode(node);
      setAddToNodeDialogOpen(true);
    },
    openEdit: (nodeId) => {
      setNodeId(nodeId);
      const node = draftTree.find((n) => n.energyTreeNodeId === nodeId) ?? null;
      setNodeToEdit(node);
      setEditNodeDialogOpen(true);
    },
    openDelete: (nodeId) => {
      setNodeId(nodeId);
      const node = draftTree.find((n) => n.energyTreeNodeId === nodeId) ?? null;
      setNodeToDelete(node);
      setDeleteNodeDialogOpen(true);
    },
    openMove: (nodeId) => {
      setNodeId(nodeId);
      const node = draftTree.find((n) => n.energyTreeNodeId === nodeId) ?? null;
      setNodeToMove(node);
      setMoveNodeDialogOpen(true);
    },
  };

  // 1) Initialize chart once
  useEffect(() => {
    const container = chartRef.current;
    if (!container || chartInstanceRef.current || !selectedEnergyGroup) return;

    postExtractConfigurationCheck({
      selectedEnergyGroup: {
        ...selectedEnergyGroup,
        energyTreeNodes: draftTree,
      },
    });

    const chart = new OrgChart()
      .container(container)
      .nodeId((d) => d.energyTreeNodeId)
      .parentNodeId((d) => d.parentId)
      .setActiveNodeCentered(true)
      .nodeHeight(() => 150)
      .nodeWidth(() => 300)
      .childrenMargin(() => 100)
      .compactMarginBetween(() => 20)
      .neighbourMargin(() => 100)
      .siblingsMargin(() => 75)
      .compactMarginPair(() => 100)
      .onNodeClick((node) => handlersRef.current.handleNodeClick(node))
      .linkUpdate(function (d) {
        d3.select(this)
          .attr(
            'stroke',
            d.data._upToTheRootHighlighted ? '#4CAF50' : '#E4E2E9',
          )
          .attr('stroke-width', d.data._upToTheRootHighlighted ? 7 : 1);
        if (d.data._upToTheRootHighlighted) {
          d3.select(this).raise();
        }
      });

    chartInstanceRef.current = chart;
  }, [
    draftTree,
    postExtractConfigurationCheck,
    selectedEnergyGroup,
    setNodeId,
  ]);
  /**
   * Binds event handlers to D3 chart nodes for interactive functionality
   * Handles button clicks (add, edit, move, delete) and expansion tracking
   *
   * @param {Object} chart - D3 org chart instance
   * @param {boolean} isDarkMode - Whether dark mode is active
   */
  const bindNodeHandlers = (chart, isDarkMode) => {
    chart.nodeUpdate(function (d) {
      const sel = d3.select(this);

      sel
        .select('.node-content')
        .style('color', (d) => {
          if (d.data._highlighted || d.data._typeHighlighted) return '#000';
          return isDarkMode ? '#E4E2E9' : '#08011E';
        })
        .style('box-shadow', (d) => {
          if (d.data._typeHighlighted) {
            return '0 0 0 7px #2196F3'; // Blue for type highlights
          }
          if (d.data._highlighted) {
            return '0 0 0 7px #4CAF50'; // Green for search highlights
          }
          if (d.data._upToTheRootHighlighted) {
            return '0 0 0 7px #4CAF50'; // Green for search ancestor highlights
          }
          return 'none';
        })
        .style('border-radius', '16px')
        .style('transition', 'box-shadow 0.3s ease-in-out');

      d3.select(chartRef.current)
        .select('svg')
        .attr('style', 'height: 100%; overflow: hidden;');

      const stopAll = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        ev.stopImmediatePropagation();
        if (ev.nativeEvent) {
          ev.nativeEvent.preventDefault();
          ev.nativeEvent.stopPropagation();
          if (ev.nativeEvent.stopImmediatePropagation) {
            ev.nativeEvent.stopImmediatePropagation();
          }
        }
      };

      sel
        .select('.btn-add')
        .on('mousedown', stopAll)
        .on('click', (e) => {
          stopAll(e);
          handlersRef.current.openAdd(d.data.energyTreeNodeId);
        });

      sel
        .select('.btn-edit')
        .on('mousedown', stopAll)
        .on('click', (e) => {
          stopAll(e);
          handlersRef.current.openEdit(d.data.energyTreeNodeId);
        });

      sel
        .select('.btn-move')
        .on('mousedown', stopAll)
        .on('click', (e) => {
          stopAll(e);
          handlersRef.current.openMove(d.data.energyTreeNodeId);
        });

      sel
        .select('.btn-delete')
        .on('pointerdown', stopAll)
        .on('mousedown', stopAll)
        .on('click', (e) => {
          stopAll(e);
          handlersRef.current.openDelete(d.data.energyTreeNodeId);
        });

      // Handle expand/collapse button clicks for manual expansion tracking
      sel.selectAll('.node-button-circle').on('click', (e) => {
        stopAll(e);

        // Track manual expansion/collapse using ref to avoid race conditions
        const nodeData = d.data;
        if (!nodeData?.energyTreeNodeId) return;

        // Check if node was collapsed (has _children) or expanded (has children)
        const wasCollapsed = !!nodeData._children;

        if (wasCollapsed) {
          // Node is being expanded - always track as manual expansion
          manuallyExpandedNodesRef.current.set(nodeData.energyTreeNodeId, true);
        } else {
          // Node is being collapsed - remove from manual tracking
          manuallyExpandedNodesRef.current.delete(nodeData.energyTreeNodeId);
        }
      });
    });
  };
  // 2) Update chart data and apply expansion in single effect to avoid cascading renders
  useEffect(() => {
    if (!chartInstanceRef.current || !draftTree?.length) return;

    const chart = chartInstanceRef.current;

    // Store current zoom/pan state before any updates
    const currentState = chart.getChartState();
    if (currentState) {
      chartStateRef.current = {
        scale: currentState.scale,
        x: currentState.x,
        y: currentState.y,
      };
    }

    // Previous data used by the chart (with mutated _expanded flags)
    const previousData = chart.data && chart.data();
    const expandedMap = new Map();

    if (Array.isArray(previousData)) {
      previousData.forEach((node) => {
        if (node?.energyTreeNodeId != null) {
          expandedMap.set(node.energyTreeNodeId, node._expanded);

          // Track expanded nodes for manual expansion preservation
          if (node._expanded) {
            manuallyExpandedNodesRef.current.set(node.energyTreeNodeId, true);
          }
        }
      });
    }

    // Merge tree data with expansion state
    const mergedTreeData = draftTree.map((node) => {
      const prevExpanded = expandedMap.get(node.energyTreeNodeId);
      const wasManuallyExpanded = manuallyExpandedNodesRef.current.get(
        node.energyTreeNodeId,
      );

      // Preserve expansion if it was previously expanded or manually expanded
      const shouldBeExpanded = prevExpanded || wasManuallyExpanded || false;

      return prevExpanded === undefined && !wasManuallyExpanded
        ? node
        : { ...node, _expanded: shouldBeExpanded };
    });

    // Update chart data
    chart.data(mergedTreeData).render();

    // Apply expansion logic based on levels and manual tracking
    const { allNodes } = chart.getChartState() || {};
    if (allNodes && allNodes.length) {
      // Update max depth
      const maxDepth = allNodes.reduce((m, n) => Math.max(m, n.depth), 1);
      maxDepthRef.current = maxDepth;

      // Apply expansion logic
      const targetDepth = Math.max(1, Math.min(expandLevel, maxDepth));

      allNodes.forEach((node) => {
        // For level-based operations, use simple depth-based expansion
        // Manual expansion only preserves when adding nodes, not for level changes
        const shouldExpand = node.depth <= targetDepth;

        if (shouldExpand) {
          if (node._children) {
            node.children = node._children;
            node._children = null;
          }
          node.data._expanded = true;
        } else {
          if (node.children) {
            node._children = node.children;
            node.children = null;
          }
          node.data._expanded = false;
        }
      });

      // Render with expansion applied
      chart.render();

      // Restore zoom/pan state if available (using fit as fallback)
      if (chartStateRef.current) {
        // The D3 org chart library may not have zoomTo/setCentered methods
        // Use fit() as a safe fallback for now
        try {
          chart.fit();
        } catch (error) {
          console.warn('Could not restore zoom state:', error);
        }
      } else {
        chart.fit();
      }

      // Bind handlers after render
      const isDarkMode = theme.palette.mode === 'dark';
      bindNodeHandlers(chart, isDarkMode);
    }
  }, [draftTree, expandLevel, theme.palette.mode]);

  // 4) Update nodeContent/buttonContent when theme/timeRange change (no handler binding)
  useEffect(() => {
    if (!chartInstanceRef.current) return;

    const chart = chartInstanceRef.current;
    const isDarkMode = theme.palette.mode === 'dark';

    // Store current expansion state before theme update
    const currentData = chart.data();
    const expansionState = new Map();
    if (currentData) {
      currentData.forEach((node) => {
        if (node.energyTreeNodeId != null) {
          expansionState.set(node.energyTreeNodeId, node._expanded);
        }
      });
    }

    chart
      .nodeContent(
        getNodeContent(
          isDarkMode,
          timeRange,
          isPortfolio,
          unit,
          validationResult,
        ),
      )
      .buttonContent(getButtonContent(isDarkMode))
      .render();

    // Restore expansion state and bind handlers
    requestAnimationFrame(() => {
      // Restore expansion state
      const newData = chart.data();
      if (newData && expansionState.size > 0) {
        newData.forEach((node) => {
          const wasExpanded = expansionState.get(node.energyTreeNodeId);
          if (wasExpanded !== undefined) {
            node._expanded = wasExpanded;
          }
        });
        chart.data(newData).render();
      }

      // Bind handlers after theme update
      bindNodeHandlers(chart, isDarkMode);
    });
  }, [theme.palette.mode, timeRange, isPortfolio, unit, validationResult]);

  // Handle container resize (e.g., when drawer opens/closes) - Setup once, persist throughout component lifecycle
  useEffect(() => {
    if (!chartRef.current || !chartInstanceRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Clear any pending resize operations
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce resize to avoid too many renders
      resizeTimeoutRef.current = setTimeout(() => {
        if (chartInstanceRef.current) {
          // Use requestAnimationFrame to prevent blocking the UI
          requestAnimationFrame(() => {
            chartInstanceRef.current.render().fit();
          });
        }
      }, 150);
    });

    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array - set up once and persist

  /**
   * Expands the chart by one level from current expansion depth
   */
  const expandOneLevel = () => {
    setExpandLevel((prev) => Math.min(prev + 1, maxDepthRef.current));
  };

  /**
   * Collapses the chart by one level from current expansion depth
   */
  const collapseOneLevel = () => {
    setExpandLevel((prev) => Math.max(1, prev - 1));
  };

  /**
   * Expands all nodes to maximum depth and clears manual expansion overrides
   */
  const expandAllNodes = () => {
    setExpandLevel(maxDepthRef.current);
    // Clear manual tracking to let level-based expansion take over
    manuallyExpandedNodesRef.current.clear();
  };

  /**
   * Collapses all nodes to root level and clears manual expansion overrides
   */
  const collapseAllNodes = () => {
    setExpandLevel(1);
    // Clear manually expanded tracking since we're collapsing all
    manuallyExpandedNodesRef.current.clear();
  };

  /**
   * Increases chart zoom level and preserves zoom state
   */
  const zoomIn = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.zoomIn();
      // Store the new zoom level
      const currentState = chartInstanceRef.current.getChartState();
      if (currentState) {
        chartStateRef.current = {
          scale: currentState.scale,
          x: currentState.x,
          y: currentState.y,
        };
      }
    }
  };

  /**
   * Decreases chart zoom level and preserves zoom state
   */
  const zoomOut = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.zoomOut();
      // Store the new zoom level
      const currentState = chartInstanceRef.current.getChartState();
      if (currentState) {
        chartStateRef.current = {
          scale: currentState.scale,
          x: currentState.x,
          y: currentState.y,
        };
      }
    }
  };

  /**
   * Fits the entire chart to screen and clears stored zoom state
   */
  const fitToScreen = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.fit();
      // Clear stored zoom state since we're fitting to screen
      chartStateRef.current = null;
    }
  };

  /**
   * Updates node highlighting based on search terms and selected types
   * Applies visual highlights and expands ancestor nodes for visibility
   *
   * @param {string} searchValue - Text to search for in node names/types
   * @param {Set} selectedTypes - Set of node types to highlight
   */
  const updateHighlighting = (searchValue, selectedTypes) => {
    if (!chartInstanceRef.current) return;

    const chart = chartInstanceRef.current;
    const data = chart.data();

    if (!data || data.length === 0) return;

    // Store current expansion state before resetting highlights
    const currentExpandedState = new Map();
    data.forEach((node) => {
      if (node.energyTreeNodeId != null) {
        currentExpandedState.set(node.energyTreeNodeId, node._expanded);
      }
    });

    // Reset only highlighting, preserve expansion state
    data.forEach((node) => {
      node._highlighted = false;
      node._typeHighlighted = false;
      node._upToTheRootHighlighted = false;
      node._upToTheRootTypeHighlighted = false;
      // DON'T reset _expanded - preserve it!
    });

    let firstMatch = null;

    const markAncestors = (node, isTypeHighlight = false) => {
      if (isTypeHighlight) {
        node._upToTheRootTypeHighlighted = true;
      } else {
        node._upToTheRootHighlighted = true;
      }
      node._expanded = true;
      if (node.parentId) {
        const parent = data.find((n) => n.energyTreeNodeId === node.parentId);
        if (parent) markAncestors(parent, isTypeHighlight);
      }
    };

    // Handle search highlighting (green)
    if (searchValue) {
      const matchingTypes = new Set();
      data.forEach((node) => {
        const type = node.type?.toLowerCase() || '';
        const search = searchValue?.toLowerCase() || '';

        if (search && type.includes(search)) {
          matchingTypes.add(node.type);
        }
      });

      data.forEach((node) => {
        const name = node.name?.toLowerCase() || '';
        const search = searchValue?.toLowerCase() || '';
        const isTypeMatch = matchingTypes.has(node.type);
        const isNameMatch = search && name.includes(search);

        if (isTypeMatch || isNameMatch) {
          node._highlighted = true;
          markAncestors(node, false); // false = search highlight
          if (!firstMatch) firstMatch = node;
        }
      });
    }

    // Handle type highlighting (blue)
    if (selectedTypes.size > 0) {
      // Helper function to expand ancestors without highlighting
      const expandAncestors = (node) => {
        node._expanded = true;
        if (node.parentId) {
          const parent = data.find((n) => n.energyTreeNodeId === node.parentId);
          if (parent) expandAncestors(parent);
        }
      };

      data.forEach((node) => {
        if (selectedTypes.has(node.type)) {
          node._typeHighlighted = true;
          // Expand ancestors for visibility but don't highlight them
          expandAncestors(node);
          if (!firstMatch) firstMatch = node;
        }
      });
    }

    // If no filters, restore original expansion state including manually expanded nodes
    if (!searchValue && selectedTypes.size === 0) {
      data.forEach((node) => {
        const originalExpanded = currentExpandedState.get(
          node.energyTreeNodeId,
        );
        const wasManuallyExpanded = manuallyExpandedNodesRef.current.get(
          node.energyTreeNodeId,
        );

        // Restore expansion if it was originally expanded or manually expanded
        if (originalExpanded !== undefined || wasManuallyExpanded) {
          node._expanded = originalExpanded || wasManuallyExpanded;
        }
      });
    }

    chart.data(data).render();

    // Apply styling inline to prevent function dependencies
    requestAnimationFrame(() => {
      const isDarkMode = theme.palette.mode === 'dark';

      chart.nodeUpdate(function (d) {
        const sel = d3.select(this);

        // Add CSS classes for test identification
        // sel
        //   .classed('orgchart-node', true)
        //   .classed('orgchart-node-highlighted', d.data._highlighted || false)
        //   .classed(
        //     'orgchart-node-dimmed',
        //     !d.data._highlighted &&
        //       !d.data._typeHighlighted &&
        //       (searchValue || selectedTypes.size > 0),
        //   )
        //   .classed('orgchart-node-filtered', d.data._typeHighlighted || false)
        //   .classed('orgchart-node-visible', d.data._expanded !== false)
        //   .classed('orgchart-node-hidden', d.data._expanded === false)
        //   .classed(`orgchart-node-level-${d.depth}`, true)
        //   .classed('orgchart-node-expandable', d.children || d._children)
        //   .attr('data-testid', 'orgchart-node')
        //   .attr('data-node-level', d.depth);

        sel
          .select('.node-content')
          .style('color', (d) => {
            if (d.data._highlighted || d.data._typeHighlighted) return '#000';
            return isDarkMode ? '#E4E2E9' : '#08011E';
          })
          .style('box-shadow', (d) => {
            if (d.data._typeHighlighted) {
              return '0 0 0 7px #2196F3'; // Blue for type highlights
            }
            if (d.data._highlighted) {
              return '0 0 0 7px #4CAF50'; // Green for search highlights
            }
            if (d.data._upToTheRootHighlighted) {
              return '0 0 0 7px #4CAF50'; // Green for search ancestor highlights
            }
            return 'none';
          })
          .style('border-radius', '16px')
          .style('transition', 'box-shadow 0.3s ease-in-out');

        d3.select(chartRef.current)
          .select('svg')
          .attr('style', 'height: 100%; overflow: hidden;');

        const stopAll = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (ev.nativeEvent?.stopImmediatePropagation) {
            ev.nativeEvent.stopImmediatePropagation();
          }
        };

        sel
          .select('.btn-add')
          .on('mousedown', stopAll)
          .on('click', (e) => {
            stopAll(e);
            handlersRef.current.openAdd(d.data.energyTreeNodeId);
          });

        sel
          .select('.btn-edit')
          .on('mousedown', stopAll)
          .on('click', (e) => {
            stopAll(e);
            handlersRef.current.openEdit(d.data.energyTreeNodeId);
          });

        sel
          .select('.btn-move')
          .on('mousedown', stopAll)
          .on('click', (e) => {
            stopAll(e);
            handlersRef.current.openMove(d.data.energyTreeNodeId);
          });

        sel
          .select('.btn-delete')
          .on('pointerdown', stopAll)
          .on('mousedown', stopAll)
          .on('click', (e) => {
            stopAll(e);
            handlersRef.current.openDelete(d.data.energyTreeNodeId);
          });
      });

      // Don't fit here - preserve current zoom state
    });
  };

  /**
   * Handles search input with debouncing to prevent excessive updates
   *
   * @param {string} value - Search input value
   */
  const handleSearch = (value) => {
    setSearchValue(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      updateHighlighting(value, selectedTypes);
    }, 200);
  };

  /**
   * Handles type filter selection changes and updates highlighting
   *
   * @param {Array} types - Array of selected type names
   */
  const handleTypeToggle = (types) => {
    const newSelectedTypes = new Set(types);
    setSelectedTypes(newSelectedTypes);
    updateHighlighting(searchValue, newSelectedTypes);
  };

  /**
   * Closes the info drawer and clears selection state
   */
  const handleClose = () => {
    setDialogOpen(false);
    setClickedNode(null); // Clear local clicked node state
    clearNodeId();
  };

  /**
   * Saves draft changes to the server and handles success/error notifications
   */
  const confirmSave = async () => {
    if (!selectedEnergyGroup?.energyGroupId || !draftTree) return;
    // Compare draft tree with server tree to identify new nodes
    const serverNodeIds = new Set(
      (selectedEnergyGroup?.energyTreeNodes || []).map(
        (node) => node.energyTreeNodeId,
      ),
    );

    const draftTreeWithTempProperties = draftTree.map((node) => {
      const isNewNode = !serverNodeIds.has(node.energyTreeNodeId);

      // Filter out D3 internal properties (properties starting with _)
      // Only include business properties that should be sent to the server
      const cleanNode = Object.keys(node).reduce((acc, key) => {
        if (!key.startsWith('_')) {
          acc[key] = node[key];
        }
        return acc;
      }, {});

      const baseNode = {
        ...cleanNode,
        tempTreePositionId: node.energyTreeNodeId,
        tempParentTreePositionId: node.parentId,
        options: {
          specialNodeType: node.options?.specialNodeType ?? null,
          consumption: node.options?.consumption ?? null,
          percentage: node.options?.percentage ?? null,
          openingHours: node.options?.openingHours ?? {},
          goShares: node.options?.goShares ?? [],
        },
      };

      if (isNewNode) {
        return { ...baseNode, energyTreeNodeId: null };
      }

      return baseNode;
    });

    const result = await updateEnergyTreeNodes({
      groupId: selectedEnergyGroup.energyGroupId,
      body: draftTreeWithTempProperties,
    });

    if (!('error' in result)) {
      // Don't call commitDraft() - let the refetched server data auto-reset the draft
      // The mutation invalidates 'EnergyGroup' tag, triggering useGetEnergyGroupsQuery refetch
      // When fresh server data arrives, useTreeDraft will auto-sync because preserveDraftOnServerChange: false

      dispatch(
        showSnackbar({
          message: t('tree_actions.save_success'),
          severity: 'success',
        }),
      );

      setSaveDialogOpen(false);
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  // Effect to run validation when draftTree changes (e.g., after adding nodes)
  useEffect(() => {
    // Don't auto-validate on initial load or if tree is empty
    if (!draftTree?.length) return;

    const runServerValidation = async () => {
      if (!selectedEnergyGroup?.energyGroupId || !draftTree) return;
      resetValidation();
      await postExtractConfigurationCheck({
        selectedEnergyGroup: {
          ...selectedEnergyGroup,
          energyTreeNodes: draftTree,
        },
      });
    };

    // Debounce validation to avoid excessive calls during rapid changes
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      runServerValidation();
    }, 500); // Wait 500ms after last change

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftTree, isDirty]);

  /**
   * Discards all draft tree changes and resets validation state
   */
  const handleDiscardChanges = () => {
    discardChanges();
    resetValidation();
  };

  /**
   * Handles user choosing to stay on the page when navigation is blocked
   */
  const handleStayOnPage = () => {
    if (blocker.status === 'blocked') {
      blocker.reset();
    }
  };

  /**
   * Handles user choosing to leave the page without saving changes
   */
  const handleLeaveWithoutSaving = () => {
    if (blocker.status === 'blocked') {
      discardChanges();
      resetValidation();
      blocker.proceed();
    }
  };

  return (
    <PageContainer sx={{ pt: 0 }}>
      <Stack>
        <Card variant="outlined">
          <Box
            data-testid="orgchart-container"
            sx={{
              position: 'relative',
              width: '100%',
              height: 'calc(100vh - 175px)',
            }}
          >
            <OrgChartToolbar
              selectedEnergyGroup={selectedEnergyGroup}
              searchValue={searchValue}
              handleSearch={handleSearch}
              availableTypes={availableTypes}
              selectedTypes={Array.from(selectedTypes)}
              onTypeToggle={handleTypeToggle}
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              onOpenFilterDialog={onOpenFilterDialog}
              fitToScreen={fitToScreen}
              expandAllNodes={expandAllNodes}
              collapseAllNodes={collapseAllNodes}
              collapseOneLevel={collapseOneLevel}
              expandOneLevel={expandOneLevel}
              isDirty={isDirty}
              validationResult={validationResult}
              isValidating={isValidating}
              isSaving={isSaving}
              onSaveChanges={() => setSaveDialogOpen(true)}
              onDiscardChanges={handleDiscardChanges}
            />
            <Box
              ref={chartRef}
              className="chart-container"
              data-testid="orgchart-chart-container"
              sx={{
                width: '100%',
                height: '100%',
              }}
            >
              {/* <div
            data-testid="orgchart-svg-container"
            // style={{ width: '100%', height: '100%' }}
          >
            <svg
              data-testid="orgchart-svg"
              // style={{ width: '100%', height: '100%' }}
            ></svg>
          </div> */}
            </Box>

            {/* Floating, semi-transparent toolbar overlay */}
          </Box>
        </Card>
      </Stack>
      <Suspense>
        {dialogOpen && clickedNode && units && (
          <InfoNodeDrawer
            key={clickedNode?.energyTreeNodeId}
            energyTreeNode={clickedNode}
            open={dialogOpen}
            onClose={handleClose}
            units={units}
            validationResult={validationResult}
            data-testid="orgchart-info-drawer"
          />
        )}

        {addToNodeDialogOpen && addToNode && (
          <AddNodeDialog
            key={addToNode?.energyTreeNodeId}
            open={addToNodeDialogOpen}
            onClose={() => {
              setAddToNodeDialogOpen(false);
              setAddToNode(null);
              clearNodeId();
            }}
            energyTreeNode={addToNode}
            units={decoratedUnits}
            mode="local"
            onConfirm={(createdNode) => {
              // Mark parent for manual expansion to show new child
              if (addToNode?.energyTreeNodeId) {
                manuallyExpandedNodesRef.current.set(
                  addToNode.energyTreeNodeId,
                  true,
                );
              }
              // Add the node to the tree
              addNode(createdNode);
            }}
            data-testid="orgchart-add-node-dialog"
          />
        )}

        {editNodeDialogOpen && nodeToEdit && (
          <EditNodeDialog
            key={nodeToEdit.energyTreeNodeId}
            open={editNodeDialogOpen}
            onClose={() => {
              setEditNodeDialogOpen(false);
              setNodeToEdit(null);
              clearNodeId();
            }}
            energyTreeNode={nodeToEdit}
            units={decoratedUnits}
            tree={draftTree}
            mode="local"
            onConfirm={(updatedNode) => {
              updateNode(updatedNode.energyTreeNodeId, updatedNode);
            }}
            data-testid="orgchart-edit-node-dialog"
          />
        )}

        {deleteNodeDialogOpen && nodeToDelete && (
          <DeleteNodeDialog
            key={nodeToDelete.energyTreeNodeId}
            open={deleteNodeDialogOpen}
            onClose={() => {
              setDeleteNodeDialogOpen(false);
              setNodeToDelete(null);
              clearNodeId();
            }}
            energyTreeNode={nodeToDelete}
            mode="local"
            onConfirm={({ energyTreeNodeId }) => {
              deleteNode(energyTreeNodeId, { includeDescendants: true });
            }}
            data-testid="orgchart-delete-node-dialog"
          />
        )}

        {moveNodeDialogOpen && nodeToMove && (
          <MoveNodeDialog
            key={nodeToMove?.energyTreeNodeId}
            open={moveNodeDialogOpen}
            onClose={() => {
              setMoveNodeDialogOpen(false);
              setNodeToMove(null);
              clearNodeId();
            }}
            energyTreeNode={nodeToMove}
            tree={draftTree}
            units={units}
            mode="local"
            onConfirm={({ energyTreeNodeId, parentId }) => {
              moveNode(energyTreeNodeId, parentId);
            }}
            data-testid="orgchart-move-node-dialog"
          />
        )}

        <SaveChangesDialog
          open={saveDialogOpen && draftTree}
          onClose={() => setSaveDialogOpen(false)}
          isValidating={isValidating}
          validationResult={validationResult}
          isSaving={isSaving}
          onConfirmSave={confirmSave}
          draftTree={draftTree}
          allowSaveWithWarnings={true} // ! still needed as we want to allow saving with warnings, but not with errors
          allowSaveWithErrors={true} // ! still needed as we want to allow saving with errors for now, but this can be changed based on validation result in the future
          data-testid="orgchart-save-changes-dialog"
        />

        <UnsavedChangesDialog
          open={isBlocked}
          onStay={handleStayOnPage}
          onLeave={handleLeaveWithoutSaving}
        />
      </Suspense>
    </PageContainer>
  );
};

export default OrgChartComponent;
